import type { ISortDirection } from '@app/lib/sorting'
import type { AppAction } from '@app/state'

export enum TodayTab {
  Incomplete,
  Complete,
}

export interface State {
  locationFilter?: string
  mrnFilter?: string
  pageNumber: number
  patientNameFilter?: string
  recordsPerPage: number
  sortDirection?: ISortDirection
  sortField?: string
  statusFilters: Record<TodayTab, string[]>
  typeFilter: string[]
}

export const initialState: State = {
  locationFilter: null,
  mrnFilter: null,
  pageNumber: 1,
  patientNameFilter: null,
  recordsPerPage: 50,
  statusFilters: {
    [TodayTab.Complete]: [],
    [TodayTab.Incomplete]: [],
  },
  typeFilter: [],
}

export enum ActionType {
  SetLocationFilter = 'TODAY/SET_LOCATION_FILTER',
  SetMrnFilter = 'TODAY/SET_MRN_FILTER',
  SetPatientNameFilter = 'TODAY/SET_PATIENT_NAME_FILTER',
  SetPageNumber = 'TODAY/SET_PAGE_NUMBER',
  SetRecordsPerPage = 'TODAY/SET_RECORDS_PER_PAGE',
  SetStatusFilter = 'TODAY/SET_STATUS_FILTER',
  SetTypeFilter = 'TODAY/SET_TYPE_FILTER',
  SetSort = 'TODAY/SET_SORT',
  SetState = 'TODAY/SET_STATE',
}

export type Action =
  | { type: ActionType.SetLocationFilter; value?: string }
  | { type: ActionType.SetMrnFilter; value?: string }
  | { type: ActionType.SetPatientNameFilter; value?: string }
  | { type: ActionType.SetPageNumber; value: number }
  | { type: ActionType.SetRecordsPerPage; value: number }
  | { type: ActionType.SetStatusFilter; tab: TodayTab; value: string[] }
  | { type: ActionType.SetSort; field?: string; direction?: ISortDirection }
  | { type: ActionType.SetTypeFilter; value: string[] }
  | { type: ActionType.SetState; state: State }

export const actions = {
  setLocationFilter(value?: string) {
    return { type: ActionType.SetLocationFilter, value }
  },
  setMrnFilter(value?: string) {
    return { type: ActionType.SetMrnFilter, value }
  },
  setPatientNameFilter(value?: string) {
    return { type: ActionType.SetPatientNameFilter, value }
  },
  setPageNumber(value: number) {
    return { type: ActionType.SetPageNumber, value }
  },
  setRecordsPerPage(value: number) {
    return { type: ActionType.SetRecordsPerPage, value }
  },
  setStatus(tab: TodayTab, value: string[]) {
    return { type: ActionType.SetStatusFilter, tab, value }
  },
  setSort(field?: string, direction?: string) {
    return { type: ActionType.SetSort, field, direction }
  },
  setType(value: string[]) {
    return { type: ActionType.SetTypeFilter, value }
  },
  setState(state: State) {
    return { type: ActionType.SetState, state }
  },
}

export function reducer(state: State = initialState, action: AppAction): State {
  switch (action.type) {
    case ActionType.SetLocationFilter:
      return {
        ...state,
        locationFilter: action.value,
      }
    case ActionType.SetMrnFilter:
      return {
        ...state,
        mrnFilter: action.value,
      }
    case ActionType.SetPatientNameFilter:
      return {
        ...state,
        patientNameFilter: action.value,
      }
    case ActionType.SetPageNumber:
      return {
        ...state,
        pageNumber: action.value,
      }
    case ActionType.SetRecordsPerPage:
      return {
        ...state,
        recordsPerPage: action.value,
      }
    case ActionType.SetStatusFilter:
      return {
        ...state,
        statusFilters: {
          ...state.statusFilters,
          [action.tab]: action.value,
        },
      }
    case ActionType.SetSort:
      return {
        ...state,
        sortField: action.field,
        sortDirection: action.direction,
      }
    case ActionType.SetState:
      return action.state

    case ActionType.SetTypeFilter:
      return {
        ...state,
        typeFilter: action.value,
      }

    default:
      return state
  }
}
